import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  SimpleChanges,
} from '@angular/core';
import {
  FormGroup,
  FormArray,
  FormBuilder,
  Validators,
  FormControl,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import _ from 'lodash';
import { CustomFormControl } from '../../../shared/shared.model';
import {
  TypesOfImaging,
  RequestedRecordTypes,
  DisplayNames,
  FieldsForAdditionalDetails,
  RecordTypesInExpectedOrder,
  TypesOfImagingInExpectedOrder,
  Records,
} from '../../records/records.model';
import { addProviderSsnSubjectValidator } from '../../records/ssnSubjectValidator';
import { CommonModule, DatePipe } from '@angular/common';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { CheckboxModule } from 'primeng/checkbox';
import { DatePickerModule } from 'primeng/datepicker';
import { DropdownModule } from 'primeng/dropdown';
import { InputTextModule } from 'primeng/inputtext';
import { PanelModule } from 'primeng/panel';
import { SelectModule } from 'primeng/select';
import { TextareaModule } from 'primeng/textarea';
import { environment } from '@env/environment';
import {
  PartyRoleCode,
  PartyTypeCode,
  RequestRef,
  states,
} from '../../place-order/place-order.model';
import { CreateUuidService } from '../../../services/create-uuid/create-uuid.service';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import {
  PartyLocatorCreation,
  CaseFileItemCreation,
  CaseFilePartyRoleItemCreation,
} from 'com.trumbull.phx.casefile.api-client';
import {
  InlineObject,
  OrderCreation,
  OrderService,
  OrderUpdate,
} from 'com.trumbull.phx.order.api-client';
import {
  RecordRetrievalPartyItemCreation,
  RecordRetrievalRequestRecordScopeItemCreation,
} from 'com.trumbull.phx.recordretrievalobjective.api-client';
import { buildDocumentLists, DocumentListValidationError, RecordRetrievalObjectiveItemCreation, RecordRetrievalRequestItemCreation } from '../../document-lists';
import { TableData } from '../record-index-table/record-index-table.component';
import { RootObject } from '../orders.model';
import { forkJoin, Subject, takeUntil } from 'rxjs';
import { VocabHelperService } from '../../../services/vocab-helper/vocab-helper.service';
import { InputMaskModule } from 'primeng/inputmask';
import { DateMaskDirective } from '../../../shared/directive/date-mask.directive';
import { AttachmentsComponent } from '../../../shared/components/attachments/attachments.component';
import { Attachment } from '../../../order-info/order-info.model';
import { ContactItem, PartyLocator, PartyName } from 'com.trumbull.phx.contactlist.api-client';
import { ContactSearchComponent } from '../../records/contact-search/contact-search.component';
import { FeatureService, FeatureFlagState } from 'com.trumbull.phx.feature.api-client';
import { ExternalUserCheckService } from '../../../services/external-user-check-service/external-user-check-service.service';
import { PlaceOrderPartyFactoryService } from '../../place-order/place-order-party-factory.service';
import { RequestNumberValidatorService } from 'src/app/services/validate-request-number/request-number-validator.service';

@Component({
  selector: 'app-add-provider',
  imports: [
    InputTextModule,
    CommonModule,
    ReactiveFormsModule,
    SelectModule,
    PanelModule,
    CardModule,
    CheckboxModule,
    ButtonModule,
    TextareaModule,
    DatePickerModule,
    FormsModule,
    DropdownModule,
    ToastModule,
    InputMaskModule,
    DateMaskDirective,
    AttachmentsComponent,
    ContactSearchComponent
  ],
  templateUrl: './add-provider.component.html',
  styleUrl: './add-provider.component.scss',
  providers: [MessageService, DatePipe],
})
export class AddProviderComponent implements OnDestroy {
  formData: any;
  @Input() subjects: any;
  @Input() selectedCategory: any;
  @Input() tableData: TableData[] = [];
  @Input() orderNumber: string = '';
  @Input() orderData?: RootObject;
  @Input() orderUpdateData?: OrderUpdate;
  @Input() vocabDocumentTagsData: any = [];
  @Output() addProvider = new EventEmitter<void>();
  @Output() onSaveProvider = new EventEmitter<boolean>();
  states = environment.noAuth ? states : [];
  recordsFormGroup!: FormGroup;
  typesOfImaging = TypesOfImaging;
  selectedTemplates: string[] = [];
  requestedRecordTypes = RequestedRecordTypes;
  selectedRecordTypes: string[] = [];
  displayNames = DisplayNames;
  showDeleteModal: boolean = false;
  itemToDelete: any;
  maxDate: Date = new Date();
  records: FormArray;
  recordsData!: Records;
  isFormSubmitted: boolean = false;
  isRangeSelected: boolean = true;
  fieldsForAdditionalDetails = FieldsForAdditionalDetails;
  subjectNames: string[] = [];
  recordTypesInExpectedOrder = RecordTypesInExpectedOrder;
  typesOfImagingInExpectedOrder = TypesOfImagingInExpectedOrder;
  invalidControls: string[] = [];
  attachments: any = [];
  isSearchProviderEnabled: boolean = false;
  private destroy$ = new Subject<void>();

  constructor(
    private readonly vocabHelperService: VocabHelperService,
    private fb: FormBuilder,
    private messageService: MessageService,
    private datePipe: DatePipe,
    private readonly orderService: OrderService,
    private featureService: FeatureService,
    private readonly externalUserCheckService: ExternalUserCheckService,
    private readonly partyFactoryService: PlaceOrderPartyFactoryService,
    private readonly validatorService: RequestNumberValidatorService,
    private readonly createUuidService: CreateUuidService
  ) {
    this.records = new FormArray([this.createRecordsFormGroup()]);

    this.recordsFormGroup = this.fb.group({
      records: this.records,
    });
  }

  /**
   * Evaluates search provider visibility based on feature flags and user account linkage.
   * 
   * This method determines whether the provider search functionality should be enabled for the current user
   * by evaluating two feature flags:
   * 
   * Feature Flags:
   * 1. SearchProviderEnabled (search.provider):
   *    - 'Y': Provider search functionality is enabled
   *    - 'N': Provider search functionality is disabled for all users
   * 
   * 2. SearchProviderVisibility (search.provider):
   *    - 'all': Provider search is available for all users (internal and external)
   *    - 'external': Provider search is available only for external/linked account users
   *    - 'internal': Provider search is available only for internal users (not linked to account)
   *    - 'none': Provider search is not available to any user
   * 
   * Decision Logic:
   *    The provider search is enabled only when ALL of the following conditions are met:
   *    - SearchProviderEnabled is 'Y' AND
   *    - SearchProviderVisibility matches the user's account type:
   *      * visibility === 'all' (for all users), OR
   *      * visibility === 'external' AND user is linked to an account (external user), OR
   *      * visibility === 'internal' AND user is NOT linked to an account (internal user)
   * 
   * Example Configurations:
   *    - SearchProviderEnabled=Y, SearchProviderVisibility=all → Enabled for all users
   *    - SearchProviderEnabled=Y, SearchProviderVisibility=external → Enabled for external users only
   *    - SearchProviderEnabled=Y, SearchProviderVisibility=internal → Enabled for internal users only
   *    - SearchProviderEnabled=N, SearchProviderVisibility=* → Disabled for all users (any visibility)
  */
  evaluateSearchProviderVisibility(): void {
    forkJoin([
      this.featureService.getFeatureFlag('search.provider', 'SearchProviderEnabled'),
      this.featureService.getFeatureFlag('search.provider', 'SearchProviderVisibility'),
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([enabledFlag, visibilityFlag]) => {
        const isSearchProviderEnabled = enabledFlag.value === 'Y';
        const searchProviderVisibility = visibilityFlag.value;
        const isUserLinkedToAccount = this.isUserLinkedToAccount();

        this.isSearchProviderEnabled = this.evaluateVisibility(
          isSearchProviderEnabled,
          searchProviderVisibility,
          isUserLinkedToAccount
        );
      });
  }

  private isUserLinkedToAccount(): boolean {
    return this.externalUserCheckService.isExternalUser();
  }

  private evaluateVisibility(
    isEnabled: boolean,
    visibility: string | undefined,
    isLinked: boolean
  ): boolean {
    return !!(
      isEnabled &&
      visibility &&
      (visibility === 'all' ||
        (visibility === 'external' && isLinked) ||
        (visibility === 'internal' && !isLinked))
    );
  }   

  /**
   * Initializes the component, sets up form controls, and processes the subject data.
   */
  ngOnInit(): void {
    this.evaluateSearchProviderVisibility();
    if (this.subjects) {
      this.subjectNames = this.subjects.map((subject: any) => subject.name);
      if (this.subjectNames) {
        this.subjectNames.length === 1
          ? this.records.controls[0]
              .get('subject')
              ?.setValue(this.subjectNames[0])
          : this.records.controls[0].get('subject')?.setValue('');
      }
    }

    this.vocabHelperService
      .getVocabData([
        'RecordTypes',
        'ImagingTypes',
        'VA_Options',
        'PhysicalLocationUsAddressStateCode',
      ])
      .subscribe((data) => {
        if (data) {
          const vocabRecordTypesData = data.find(
            (d) => d.vocabularyName === 'RecordTypes'
          );
          if (vocabRecordTypesData) {
            this.requestedRecordTypes =
              vocabRecordTypesData.vocabularyItems.map((item) => ({
                name: item.description,
                key: item.code,
              }));
            this.sortRecordsInExpectedOrder();
          }
          const vocabImagingTypesData = data.find(
            (d) => d.vocabularyName === 'ImagingTypes'
          );
          if (vocabImagingTypesData) {
            this.typesOfImaging = vocabImagingTypesData.vocabularyItems.map(
              (item) => ({ name: item.description, code: item.code })
            );
          }
          this.getSortedDiagnosticList(this.typesOfImaging);
          const vocabData = data.find(
            (d) => d.vocabularyName === 'PhysicalLocationUsAddressStateCode'
          );
          if (vocabData) {
            this.states = vocabData.vocabularyItems.map((item) => ({
              name: item.description,
              value: item.code,
            }));
          }
        }
      });
    this.setupValidation();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onCancel() {
    this.addProvider.emit();
  }

  private applySSNValidator(): void {
    if (this.recordsFormGroup && this.records && this.tableData?.length) {
      this.recordsFormGroup.setValidators([
        addProviderSsnSubjectValidator(this.records, this.tableData),
      ]);
      this.recordsFormGroup.updateValueAndValidity(); 
    }
  }

  getFormatedDate(date: any) {
    if (!date) return '';
    // let formattedDate: string = '';
    // if(date) {
    //   formattedDate = this.datePipe.transform(date, 'yyyy-MM-ddTHH:mm:ss.sss')??'';
    //   formattedDate = formattedDate + 'Z';
    // }

    const parsedDate = new Date(date);

    // Check if date is valid
    if (isNaN(parsedDate.getTime())) {
      console.error('Invalid date:', date);
      return ''; 
    }

    return date.toISOString();

  }
  getRetrievalObjectiveRequest(parties?: RecordRetrievalPartyItemCreation[]) {
    let requests: RecordRetrievalRequestItemCreation[] = [];

    _.forEach(this.recordsData.records, (item) => {
      let request: RecordRetrievalRequestItemCreation = {
        documentLists: buildDocumentLists(
          (item.orderInfoAttachmentFile ?? [])
            .filter(file => file.isNew === true)
            .map(file => ({ ...file, sizeText: file.sizeText ?? '' })),
          { request: item },
        ),
      };
      let updatedSSN: string = '';
      let ssnLocator: PartyLocatorCreation = {};
      
      // Use UUID from the form item
      request.requestUuid = item.requestUuid;
      request.notes = item.notesTextarea;
      request.subpoenaLanguage = item.subpoenaLanguageTextarea;
      request.scope = [];
      _.forEach(item.selectedTypes, (type) => {
        let scope: RecordRetrievalRequestRecordScopeItemCreation;
        scope = {
          recordTypeCode: type,
          beginDate: this.getFormatedDate(item.dateToServiceFrom) ?? '',
          endDate: this.getFormatedDate(item.dateToServiceTo) ?? '',
        };
        switch (type) {
          case 'diagnostic-images':
            scope.qualifiers = {
              diagnosticTypeCodes: item.diagnosticImages?.typesOfImaging
                ? _.join(
                    _.map(item.diagnosticImages?.typesOfImaging, 'name'),
                    ', '
                  )
                : '',
              diagnosticFocusArea: item.diagnosticImages?.specificBodyPart,
              diagnosticComments: item.diagnosticImages?.other,
            };
            break;
          case 'ambulance':
            scope.qualifiers = {
              ambulanceDateOfService: this.getFormatedDate(
                item.ambulance?.date
              ),
              ambulanceTimeOfService: item.ambulance?.time,
              ambulancePickupLine1: item.ambulance?.pickupAddress,
              ambulancePickupCity: item.ambulance?.pickupCity,
              ambulancePickupState: item.ambulance?.pickupState?.value,
              ambulancePickupPostal: item.ambulance?.pickupZip,
              ambulanceDropOffLine1: item.ambulance?.dropAddress,
              ambulanceDropOffCity: item.ambulance?.dropCity,
              ambulanceDropOffState: item.ambulance?.dropState?.value,
              ambulanceDropOffPostal: item.ambulance?.dropZip,
            };
            break;
          case 'insurance':
            scope.qualifiers = {
              insuranceClaimNumber: item.insurance?.claimNumber,
              insurancePolicyNumber: item.insurance?.policyNumber,
            };
            break;
          case 'military':
            scope.qualifiers = {
              militaryBranch: item.military?.branch,
              militaryServiceNumber: item.military?.serviceNumber,
            };
            break;
          case 'union':
            scope.qualifiers = {
              unionMembershipNumber: item.union?.membershipNumber,
            };
            break;
          case 'va':
            scope.qualifiers = {
              vaMedicalRecords: item.va?.medical[0] || '',
              vaBenefitRecords: item.va?.benefit[0] || ''
            };
            break;
          case 'other':
            scope.qualifiers = {
              otherDescriptionOfRecords: item.other?.descriptionOfRecords,
            };
            break;
          case 'employment':
            updatedSSN = item.employment?.employmentSS;
            break;
          case 'irs':
            updatedSSN = item.irs?.irsSS;
            break;
          case 'social-security':
            updatedSSN = item.socialSecurity?.socialSecuritySS;
            ssnLocator = this.partyFactoryService.getLocationLocator(
              item.socialSecurity?.address,
              item.socialSecurity?.city,
              item.socialSecurity?.state?.value,
              item.socialSecurity?.zip
            );
            break;
        }

        request.scope?.push(scope);
      });
      requests.push(request);

      //update Parties for requests
      // let custodianParty = _.find(
      //   parties,
      //   (party: RecordRetrievalPartyItemCreation) => {
      //     return (
      //       party.party?.displayName == item.providerName &&
      //       _.some(party.roles, ['roleCode', PartyRoleCode.RecordsCustodian])
      //     );
      //   }
      // );

      // if (custodianParty) {
      //   let role = _.find(custodianParty.roles, [
      //     'roleCode',
      //     PartyRoleCode.RecordsCustodian,
      //   ]);
      //   if (role && role?.requestNumbers) {
      //     role?.requestNumbers?.push(request.requestNumber);
      //   } else if (role) {
      //     role.requestNumbers = [request.requestNumber];
      //   }
      // }

      // //medical Parties for requests
      // let medicalProviderParty = _.find(
      //   parties,
      //   (party: RecordRetrievalPartyItemCreation) => {
      //     return (
      //       party.party?.displayName == item.doctor &&
      //       _.some(party.roles, ['roleCode', PartyRoleCode.MedicalProvider])
      //     );
      //   }
      // );

      // if (medicalProviderParty) {
      //   let role = _.find(medicalProviderParty.roles, [
      //     'roleCode',
      //     PartyRoleCode.MedicalProvider,
      //   ]);
      //   if (role && role?.requestNumbers) {
      //     role?.requestNumbers?.push(request.requestNumber);
      //   } else if (role) {
      //     role.requestNumbers = [request.requestNumber];
      //   }
      // }

      //record subject parties for requests
      let recordSubjectParty = _.find(
        parties,
        (party: RecordRetrievalPartyItemCreation) => {
          return (
            party.party?.displayName == item.subject &&
            _.some(party.roles, ['roleCode', PartyRoleCode.RecordSubject])
          );
        }
      );

      if (recordSubjectParty) {
        let role = _.find(recordSubjectParty.roles, [
          'roleCode',
          PartyRoleCode.RecordSubject,
        ]);
        
        // Accumulate request UUIDs into the record-subject role
        if (role && item.requestUuid) {
          this.partyFactoryService.appendRequestRefToSubjectRole(
            role,
            { kind: 'uuid', value: item.requestUuid }
          );
        } else {
          console.error('Record subject role not found or requestUuid missing for subject:', item.subject);
        }

        if (updatedSSN && updatedSSN.length > 0 && role?.recordSubject) {
          role.recordSubject.socialSecurityNumber = updatedSSN;
        }
        if (ssnLocator?.typeCode) {
          recordSubjectParty.party?.locators?.push(ssnLocator);
        }
      }
    });

    return requests;
  }

  getSubmitOrderPayload() {
    let result: OrderCreation;

    // sending empty case file object for add provider scenario explicitly
    let caseObject: CaseFileItemCreation = {

    } as CaseFileItemCreation;

    let retrievalObjective: RecordRetrievalObjectiveItemCreation = {
      documentLists: buildDocumentLists([], 'order'),
      orderTypeCode: this.orderData?.retrievalObjective?.orderTypeCode,
      serviceLevelCode: this.orderData?.retrievalObjective?.serviceLevelCode,
      specialInstructions:
        this.orderData?.retrievalObjective?.specialInstructions,
      parties: this.getRetrievalObjectiveParties(),
    } as RecordRetrievalObjectiveItemCreation;

    retrievalObjective.requests = this.getRetrievalObjectiveRequest(
      retrievalObjective.parties
    );

    caseObject?.parties?.forEach(tgtEntry => {
      if (tgtEntry?.party?.typeCode)
        tgtEntry.party.typeCode = this.getPartyTypeCode(tgtEntry.party.typeCode);
    });

    retrievalObjective?.parties?.forEach(tgtEntry => {
      if (tgtEntry?.party?.typeCode)
        tgtEntry.party.typeCode = this.getPartyTypeCode(tgtEntry.party.typeCode);
    });


    result = {
      data: {
        case: caseObject,
        retrievalObjective: retrievalObjective,
      },
    };

    return result;
  }

    
  getPartyTypeCode(typeCode:any) {
    // payload.parties.forEach(tgtEntry => {
    let returnType: any = typeCode;
    switch (_.toUpper(typeCode)) {
      case 'P':
        returnType = 'Person';
        break;
      case 'O':
        returnType = 'Organization';
        break;
      case 'A':
        returnType = 'Anonymous';
        break;
      default:
        // leave it as is if no match
        break;
    }

    return returnType;
    // });
  }

  getRetrievalObjectiveParties() {
    // Records - create parties using RequestRef pattern
    let recordParties: RecordRetrievalPartyItemCreation[] = [];
    
    _.forEach(this.recordsData.records, (item) => {
      // Guard against missing requestUuid
      if (!item.requestUuid) {
        console.error('Record missing requestUuid - party linking will fail', item);
        return;
      }

      // Create RequestRef with UUID kind for new requests in add-provider flow
      const ref: RequestRef = { kind: 'uuid', value: item.requestUuid };

      // Build records custodian party with RequestRef
      let recordsCustodianParty = this.partyFactoryService.buildRecordsCustodianParty(item, ref);
      recordParties.push(recordsCustodianParty);

      // Build medical provider party if doctor is specified
      if (item.doctor && item.doctor.length > 0) {
        let medicalProviderParty = this.partyFactoryService.buildMedicalProviderParty(item, ref);
        recordParties.push(medicalProviderParty);
      }

      // Include existing record-subject parties from the order
      let recordSubjectParties = this.orderData?.retrievalObjective?.parties?.filter(
        party => party.roles?.some(role => role.roleCode === PartyRoleCode.RecordSubject)
      );
      recordParties.push(...(recordSubjectParties as RecordRetrievalPartyItemCreation[]));
    });

    return recordParties;
  }


  onSave() {
    if (this.checkValidity()) {
      this.messageService.clear();
      let requestPayload: OrderCreation;
      try {
        requestPayload = this.getSubmitOrderPayload();
      } catch (error) {
        if (!(error instanceof DocumentListValidationError)) throw error;
        this.isFormSubmitted = false;
        this.messageService.add({
          severity: 'error', summary: 'ERROR', detail: error.message, sticky: true,
        });
        return;
      }
      // const requestPayloadWithQualifiers = this.orderUpdateData;  
      const requestPayloadWithQualifiers: OrderUpdate = {
        customerReferenceNumber: this.orderUpdateData?.customerReferenceNumber,
        summaryDescription: this.orderUpdateData?.summaryDescription,
        orderedBy: this.orderUpdateData?.orderedBy,
        qualifiers: this.orderUpdateData?.qualifiers,
        data: requestPayload?.data
      }
      const updateOrder$ = this.orderService.updateOrder('standard', this.orderNumber, requestPayloadWithQualifiers as OrderUpdate);
      // const updateOrderData$ = this.orderService.updateOrderData('standard', this.orderNumber, requestPayload as InlineObject);
      // forkJoin([updateOrder$, updateOrderData$]).subscribe({
      //   next: ([updateOrderResponse, updateOrderDataResponse]) => {
      //     this.onCancel();
      //     this.onSaveProvider.emit(true);
      //   },
      //   error: (error) => {
      //     this.messageService.add({
      //       severity: 'error',
      //       summary: 'ERROR',
      //       detail:
      //         'System error, please contact help desk at EXLRecords.Helpdesk@exlservice.com',
      //       sticky: true,
      //     });
      //   },
      //   complete: () => {},
      // });
      updateOrder$.subscribe({
        next: (updateOrderResponse) => {
          // Validate request number formats after successful order update
          this.validateRequestNumberFormat(updateOrderResponse);
          
          this.onCancel();
         this.onSaveProvider.emit(true);
        },
        error: (error) => {
          this.messageService.add({
            severity: 'error',
            summary: 'ERROR',
            detail:
              'System error, please contact help desk at EXLRecords.Helpdesk@exlservice.com',
            sticky: true,
          });
        },
        complete: () => {
          
        }
      });


    } else {
      this.messageService.clear();
      const errorMessage = this.invalidControls.length
        ? `${_.join(this.invalidControls, ', ')} is mandatory. `
        : '';
      this.messageService.add({
        severity: 'error',
        summary: 'ERROR',
        detail: `${errorMessage}Please fill out all the mandatory fields before proceeding`,
        sticky: true,
      });
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    let nameList = _.map(_.get(this.subjects, 'subjects'), 'name');
    if (nameList && !_.isNull(nameList[0])) {
      this.subjectNames = nameList;
      this.subjectNames.length === 1
        ? this.records.controls[0]
            .get('subject')
            ?.setValue(this.subjectNames[0])
        : this.records.controls[0].get('subject')?.setValue('');
    }
    if (changes['tableData'] && this.tableData?.length) {
      this.applySSNValidator();
    }
  }

  checkValidity() {
    this.recordsData = this.recordsFormGroup.value;
    if (!this.recordsFormGroup.valid) {
      this.recordsFormGroup.markAllAsTouched();
      this.getInvalidControls(this.recordsFormGroup);
    }
    return this.recordsFormGroup.valid;
  }

  getInvalidControls(form: any, formArray: boolean = false) {
    if (!formArray) this.invalidControls = [];
    Object.keys(form.controls).forEach((controlName) => {
      var control = form.get(controlName);
      if (control && _.get(control, 'controls')) {
        this.getInvalidControls(control, true);
      } else if (control && control.invalid) {
        this.invalidControls.push(
          control?.displayName ? control?.displayName : controlName
        ); 
      }
    });
  }

  getSortedDiagnosticList(list: any): { name: string; code: string }[] {
    return list.sort((a: any, b: any) => {
      const indexA = this.typesOfImagingInExpectedOrder.indexOf(a.code);
      const indexB = this.typesOfImagingInExpectedOrder.indexOf(b.code);
      if (a.key === 'other') return 1;
      if (b.key === 'other') return -1;

      if (indexA !== -1 && indexB !== -1) {
        return indexA - indexB;
      }

      if (indexA !== -1) return -1;
      if (indexB !== -1) return 1;
      return 0;
    });
  }

  /**
   * Creates a new records form group with various form controls and validators.
   * @param item Optional initial data to populate the form.
   * @returns The form group instance.
   */
  createRecordsFormGroup(item?: any): FormGroup {
    return new FormGroup({
      requestUuid: new FormControl(
        item?.requestUuid || this.createUuidService.generateRequestUuid()
      ),
      providerName: new CustomFormControl('', 'Provider (Institution)', [
        Validators.required,
      ]),
      doctor: new FormControl(''),
      streetAddress: new FormControl(''),
      aptSuiteOther: new FormControl(''),
      cityName: new FormControl(''),
      stateName: new FormControl(''),
      zipCode: new FormControl(''),
      email: new CustomFormControl('', 'Email', [Validators.email]),
      phone: new FormControl(item ? item?.phone : null),
      extn: new FormControl(item ? item?.extn : null),
      fax: new FormControl(item ? item?.fax : null),
      subject: new CustomFormControl(
        this.subjectNames.length === 1 ? this.subjectNames[0] : null,
        'Record Subject',
        [Validators.required]
      ),
      range: new FormControl(['anyorall']),
      dateToServiceFrom: new CustomFormControl('', 'Date to service from', []),
      dateToServiceTo: new CustomFormControl('', 'Date to service to', []),
      medical: new FormControl(''),
      billing: new FormControl(''),
      dental: new FormControl(''),
      educational: new FormControl(''),
      employment: new FormGroup({
        employmentSS: new CustomFormControl('', 'Employment SS#'),
      }),
      irs: new FormGroup({
        irsSS: new CustomFormControl('', 'IRS SS#'),
      }),
      mentalHealth: new FormControl(''),
      pharmacy: new FormControl(''),
      union: new FormGroup({
        membershipNumber: new FormControl(''),
      }),
      other: new FormGroup({
        descriptionOfRecords: new CustomFormControl(
          '',
          'Description of Records'
        ),
      }),
      subpoenaLanguageTextarea: new FormControl(''),
      notesTextarea: new FormControl(''),
      diagnosticImages: new FormGroup({
        typesOfImaging: new FormControl([]),
        other: new FormControl(''),
        specificBodyPart: new FormControl(''),
      }),
      ambulance: new FormGroup({
        date: new CustomFormControl('', 'Date of Ambulance Service'),
        time: new FormControl(''),
        pickupAddress: new FormControl(''),
        pickupCity: new FormControl(''),
        pickupState: new FormControl(''),
        pickupZip: new FormControl(''),
        dropAddress: new FormControl(''),
        dropCity: new FormControl(''),
        dropState: new FormControl(''),
        dropZip: new FormControl(''),
      }),
      socialSecurity: new FormGroup({
        socialSecuritySS: new CustomFormControl('', 'Social Security SS#'),
        address: new CustomFormControl('', 'Social Security Street Address'),
        city: new CustomFormControl('', 'Social Security City'),
        state: new CustomFormControl('', 'Social Security State'),
        zip: new CustomFormControl('', 'Social Security Zip'),
      }),
      insurance: new FormGroup({
        claimNumber: new CustomFormControl('', 'Insurance Claim Number'),
        policyNumber: new FormControl(''),
      }),
      military: new FormGroup({
        branch: new FormControl(''),
        serviceNumber: new FormControl(''),
      }),
      va: new FormGroup({
        medical: new FormControl(''),
        benefit: new FormControl(''),
      }),
      selectedTypes: new CustomFormControl([], 'Requested record types', [
        Validators.required,
      ]),
      orderInfoAttachmentFile: new CustomFormControl([], 'Attachments'),
      orderInfoAttachmentLabel: new CustomFormControl('', 'Label'),
      orderInfoAttachmentExplanation: new CustomFormControl('', 'Attachment Description'),
    });
  }

  /**
   * Disables specific form groups in the records form array.
   * @param i The index of the record to disable.
   */
  disableFormGroups(i: number): void {
    const recordsGroup = (this.recordsFormGroup.get('records') as FormArray).at(
      i
    );
    recordsGroup.get('socialSecurity')?.disable();
    recordsGroup.get('ambulance')?.disable();
    recordsGroup.get('insurance')?.disable();
    recordsGroup.get('other')?.disable();
    recordsGroup.get('diagnosticImages')?.disable();
    recordsGroup.get('employment')?.disable();
    recordsGroup.get('union')?.disable();
    recordsGroup.get('irs')?.disable();
  }

  /**
   * Checks if the form control has an error and should be highlighted.
   * @param controlId The ID of the form control to check.
   * @param record Optional record for nested form controls.
   * @returns True if the form control is invalid and touched or dirty.
   */
  isError(controlId: string, record?: any): boolean {
    let formControl = this.recordsFormGroup.get(controlId);
    if (record) {
      formControl = record.get(controlId);
    }
    if (formControl) {
      return formControl.invalid && (formControl.dirty || formControl.touched);
    }
    return false;
  }

  /**
   * Handles the selection of a requested record type.
   * @param event The selection event.
   * @param selectedRecordTypes The record type being selected.
   * @param record The current record.
   */
  onSelectRequestedRecord(event: any, selectedRecordType: string, record: any) {
    if (event.originalEvent.target.checked) {
      this.selectedRecordTypes.push(selectedRecordType);
      if (selectedRecordType === 'Diagnostic Images')
        this.toggleAllImagingTypes(record);
    } else {
      this.selectedRecordTypes = this.selectedRecordTypes.filter(
        (selectedRecord) => selectedRecordType !== selectedRecord
      );
      record.controls[this.getKeyByName(selectedRecordType)].disable();
    }
    if (['Social Security', 'Employment', 'IRS'].includes(selectedRecordType)) {
      this.syncRecordFieldsAndSSN(selectedRecordType);
    }
  }

  onRecordSubjectChange(recordControl: any, index: number) {
    const subject = recordControl.get('subject')?.value;
    this.records.controls.forEach((otherRecordControl, otherIndex) => {
      if (index !== otherIndex) {
        const otherSubject = otherRecordControl.get('subject')?.value;
        if (subject === otherSubject) {
          const employmentSS = otherRecordControl.get(
            'employment.employmentSS'
          )?.value;
          const irsSS = otherRecordControl.get('irs.irsSS')?.value;
          const socialSecuritySS = otherRecordControl.get(
            'socialSecurity.socialSecuritySS'
          )?.value;

          const selectedValue = employmentSS || irsSS || socialSecuritySS;

          if (selectedValue) {
            recordControl
              .get('employment.employmentSS')
              ?.setValue(selectedValue);
            recordControl.get('irs.irsSS')?.setValue(selectedValue);
            recordControl
              .get('socialSecurity.socialSecuritySS')
              ?.setValue(selectedValue);
          }
        }
      }
    });
  }

  syncRecordFieldsAndSSN(selectedRecordType: string) {
    this.records.controls.forEach((recordControl, index) => {
      const subject = recordControl.get('subject')?.value;
      this.records.controls.forEach((otherRecordControl, otherIndex) => {
        if (index !== otherIndex) {
          const otherSubject = otherRecordControl.get('subject')?.value;
          if (subject === otherSubject) {
            this.syncSSNFormValues(recordControl, otherRecordControl);
          }
        }
      });
      this.syncSSNFormValues(recordControl, undefined, selectedRecordType);
    });
  }

  // Method to sync SSN form values (employmentSS, irsSS, socialSecuritySS)
  syncSSNFormValues(
    record: any,
    existingRecord?: any,
    selectedRecordType?: string
  ) {
    const employmentSS = record.get('employment.employmentSS')?.value;
    const irsSS = record.get('irs.irsSS')?.value;
    const socialSecuritySS = record.get(
      'socialSecurity.socialSecuritySS'
    )?.value;

    const val = employmentSS || irsSS || socialSecuritySS;
    if (selectedRecordType === 'Employment') {
      record.get('employment.employmentSS').setValue(val);
    } else if (selectedRecordType === 'Social Security') {
      record.get('socialSecurity.socialSecuritySS').setValue(val);
    } else if (selectedRecordType === 'IRS') {
      record.get('irs.irsSS').setValue(val);
    }

    if (existingRecord) {
      const existingEmploymentSS = existingRecord.get(
        'employment.employmentSS'
      )?.value;
      const existingIrsSS = existingRecord.get('irs.irsSS')?.value;
      const existingSocialSecuritySS = existingRecord.get(
        'socialSecurity.socialSecuritySS'
      )?.value;

      if (employmentSS && !existingEmploymentSS) {
        existingRecord.get('employment.employmentSS')?.setValue(employmentSS);
      }

      if (irsSS && !existingIrsSS) {
        existingRecord.get('irs.irsSS')?.setValue(irsSS);
      }

      if (socialSecuritySS && !existingSocialSecuritySS) {
        existingRecord
          .get('socialSecurity.socialSecuritySS')
          ?.setValue(socialSecuritySS);
      }
    }
  }

  /**
   * Creates a new form group for social security details.
   * @returns The social security form group.
   */
  createSocialSecurity(): FormGroup {
    return new FormGroup({
      socialSecuritySS: new CustomFormControl('', 'Social Security SS#', [
        Validators.required,
      ]),
      address: new CustomFormControl('', 'Social Security Street Address', [
        Validators.required,
      ]),
      city: new CustomFormControl('', 'Social Security City', [
        Validators.required,
      ]),
      state: new CustomFormControl('', 'Social Security State', [
        Validators.required,
      ]),
      zip: new CustomFormControl('', 'Social Security Zip', [
        Validators.required,
      ]),
    });
  }

  /**
   * Checks if the "Other" imaging type is selected for the record.
   * @param record The record to check.
   * @returns True if the "Other" imaging type is selected.
   */
  isOtherImagingTypeSelected(record: any): boolean {
    const typesOfImaging =
      record.controls['diagnosticImages']?.controls['typesOfImaging'].value ||
      [];
    const isOtherImaging = typesOfImaging.some(
      (type: any) => type.code === 'other' && type.name === 'Other'
    );

    return isOtherImaging;
  }

  onTypeChange(record: any, type: any, event: any) {
    const selectedTypes: any[] = [
      ...record.controls['diagnosticImages']?.controls['typesOfImaging'].value,
    ];
    record.get('diagnosticImages.typesOfImaging')?.setValue(selectedTypes);
    const isOtherImaging = record.controls['diagnosticImages']?.controls[
      'typesOfImaging'
    ].value.some((type: any) => type.code === 'other' && type.name === 'Other');

    isOtherImaging
      ? record
          .get('diagnosticImages')
          .addControl('other', new FormControl('', Validators.required))
      : record.get('diagnosticImages').removeControl('other');
  }

  /**
   * Toggles the selection of all imaging types based on specific conditions.
   * @param record The record to toggle imaging types for.
   */
  toggleAllImagingTypes(record: any) {
    const selectedValue = record.get('range')?.value.includes('anyorall');
    if (selectedValue) {
      let selectedType = this.typesOfImaging.filter(
        (type) => type.name === 'X-Rays'
      );
      if (record.get('diagnosticImages')) {
        record.get('diagnosticImages.typesOfImaging')?.setValue(selectedType);
      }
      this.resetDateValidators(record, false);
    } else {
      record.get('diagnosticImages.typesOfImaging')?.setValue([]);
      this.resetDateValidators(record, true);
    }
  }

  resetDateValidators(record: any, isSelected: boolean) {
    if (isSelected) {
      record.get('dateToServiceFrom')?.setValidators([Validators.required]);
      record
        .get('dateToServiceTo')
        ?.setValidators([Validators.required, this.dateValidator.bind(record)]);
    } else {
      record.get('dateToServiceFrom')?.clearValidators();
      record.get('dateToServiceTo')?.clearValidators();
    }
    record.get('dateToServiceFrom')?.updateValueAndValidity();
    record.get('dateToServiceTo')?.updateValueAndValidity();
  }

  dateValidator(formGroup: any) {
    if (formGroup && formGroup.parent) {
      const fromDate = formGroup.parent.get('dateToServiceFrom')?.value;
      const toDate = formGroup.parent?.get('dateToServiceTo')?.value;

      if (fromDate && toDate) {
        if (new Date(toDate) < new Date(fromDate))
          return { invalidDateRange: true };
      }
    }
    return null;
  }

  /**
   * Determines whether to show additional details for a record based on its selected types.
   * @param record The record to check.
   * @returns True if additional details should be shown.
   */
  showAdditionalDetails(record: any): boolean {
    return (
      this.fieldsForAdditionalDetails.filter((type) =>
        record.controls['selectedTypes'].value.includes(type)
      ).length > 0
    );
  }

  /**
   * Checks if a specific record type is selected and toggles the visibility of its corresponding field.
   * @param record The record to check.
   * @param type The record type to check.
   * @returns True if the record type is selected.
   */
  checkRecordType(record: any, type: any) {
    const selectedType = record.controls['selectedTypes'].value.includes(type);
    const typeMap: { [key: string]: string } = {
      'diagnostic-images': 'diagnosticImages',
      'social-security': 'socialSecurity',
    };
    const mappedType = typeMap[type] || type;
    const control = record.get(mappedType);
    if (control) {
      const isControlCurrentlyEnabled = !control.disabled;
      if (isControlCurrentlyEnabled !== selectedType) {
        this.toggleFieldVisibility(selectedType, control);
      }
    }
    return selectedType;
  }

  /**
   * Toggles the visibility of a field based on its selected state.
   * @param isVisible True to enable the field, false to disable it.
   * @param control The form control to toggle.
   */
  toggleFieldVisibility(isVisible: boolean, control: any) {
    if (control) {
      if (isVisible) {
        control.enable();
      } else {
        control.disable();
      }
    }
  }

  getKeyByName(name: string): string {
    const item = this.displayNames.find((entry) => entry.name === name);
    return item ? item.key : '';
  }

  setupValidation() {
    const recordsArray = this.recordsFormGroup.get('records') as FormArray;
    if (!recordsArray) {
      console.error('records FormArray is undefined');
      return;
    }
    recordsArray.controls.forEach((recordGroup, index) => {
      const group = recordGroup as FormGroup;
      group.get('dateToServiceFrom')?.valueChanges.subscribe(() => {
        group.get('dateToServiceTo')?.updateValueAndValidity();
      });
      const selectedTypesControl = group.get('selectedTypes');
      if (!selectedTypesControl) {
        console.error(`selectedTypes not found for record at index ${index}`);
        return;
      }
      selectedTypesControl.valueChanges.subscribe((selectedTypes: string[]) => {
        const groupsToValidate: any = {
          socialSecurity: ['socialSecuritySS', 'address', 'city', 'state'],
          ambulance: ['date'],
          insurance: ['claimNumber'],
          other: ['descriptionOfRecords'],
          employment: ['employmentSS'],
          irs: ['irsSS'],
        };

        Object.keys(groupsToValidate).forEach((groupKey) => {
          const fieldGroup = group.get(groupKey) as FormGroup;
          if (!fieldGroup) return;
          groupsToValidate[groupKey].forEach((field: any) => {
            const typeMap: { [key: string]: string } = {
              diagnosticImages: 'diagnostic-images',
              socialSecurity: 'social-security',
            };
            const mappedType = typeMap[groupKey] || groupKey;
            const control = fieldGroup.get(field);
            if (control) {
              if (selectedTypes.includes(mappedType)) {
                control.addValidators(Validators.required);
              } else {
                control.clearValidators();
              }
              control.updateValueAndValidity();
            }
          });
        });
      });
    });
  }

  sortRecordsInExpectedOrder() {
    this.requestedRecordTypes.sort((a, b) => {
      const indexA = this.recordTypesInExpectedOrder.indexOf(a.key);
      const indexB = this.recordTypesInExpectedOrder.indexOf(b.key);

      // If key is "other", always push it to the end
      if (a.key === 'other') return 1;
      if (b.key === 'other') return -1;

      // If both keys are in the expected order, sort accordingly
      if (indexA !== -1 && indexB !== -1) {
        return indexA - indexB;
      }

      // If only one of them is in expectedOrder, prioritize it
      if (indexA !== -1) return -1;
      if (indexB !== -1) return 1;

      // If neither are in expectedOrder, maintain their relative order
      return 0;
    });
  }

  checkSubjectMismatchError(subject: string): boolean {
    const ssnSubjectMismatch =
      this.recordsFormGroup.errors?.['ssnSubjectMismatch'];
    return ssnSubjectMismatch && ssnSubjectMismatch.includes(subject);
  }

  onDateChange(event: any, index: number, dateType: string) {
    const rawValue = event.target.value;
    const inputDate = new Date(rawValue);
    const recordsFormGroup = (this.recordsFormGroup.get('records') as FormArray).at(index);
      
    if (!isNaN(inputDate.getTime()) && inputDate > this.maxDate) {
      if (dateType === 'serviceFromDate') {
        recordsFormGroup.get('dateToServiceFrom')?.setErrors({ maxDateExceeded: true });  
      } else if(dateType === 'serviceToDate'){
        recordsFormGroup.get('dateToServiceTo')?.setErrors({ maxDateExceeded: true });  
      } else {
        (recordsFormGroup.get('ambulance') as FormGroup)
        ?.get('date')
        ?.setErrors({ maxDateExceeded: true });
      }
    }
  }

    onContactSelect(contact: ContactItem, index: number) {
      const recordsGroup = (this.recordsFormGroup.get('records') as FormArray).at(index);
      if (!recordsGroup || !contact) return;
  
      // Patch provider name
      if (contact.party?.displayName) {
        recordsGroup.get('providerName')?.setValue(contact.party.displayName);
      }
  
      // Patch doctor - find medical-provider role similar to document-processor.service.ts
      const doctorName = contact.party?.names?.find((n: PartyName) => n.function === 'medical-provider')?.text;
      if (doctorName) {
        recordsGroup.get('doctor')?.setValue(doctorName);
      }
  
      // Patch address fields
      const locationLocator = contact.party?.locators?.find((l: PartyLocator) => l.typeCode === 'location');
      const address = locationLocator?.location?.location?.usAddress;
      
      if (address) {
        recordsGroup.get('streetAddress')?.setValue(address.lineOne || '');
        recordsGroup.get('aptSuiteOther')?.setValue(address.lineTwo || '');
        recordsGroup.get('cityName')?.setValue(address.city || '');
        recordsGroup.get('zipCode')?.setValue(address.postalCode || '');
        
        console.debug('address', address);
        console.debug('states', this.states);
        if (address.stateCode) {
          const matchingState = this.states?.find((s: any) => 
            s.value?.toUpperCase() === address.stateCode?.toUpperCase()
          );
          if (matchingState) {
            recordsGroup.get('stateName')?.setValue(matchingState);
          }
        }
      }
  
      // Patch phone fields - phone may already be formatted
      const phoneLocator = contact.party?.locators?.find((l: PartyLocator) => l.typeCode === 'phone' && l.phone?.purpose == 'work');
      if (phoneLocator?.phone?.number && phoneLocator.phone.purpose == 'work') {
        recordsGroup.get('phone')?.setValue(phoneLocator.phone.number);
      } else {
        recordsGroup.get('phone')?.setValue('');
      }
      if (phoneLocator?.phone?.extension && phoneLocator.phone.purpose == 'work') {
        recordsGroup.get('extn')?.setValue(phoneLocator.phone.extension);
      } else {
        recordsGroup.get('extn')?.setValue('');
      }
  
      // Patch fax field
      const faxLocator = contact.party?.locators?.find((l: PartyLocator) => l.typeCode === 'phone' && l.phone?.purpose == 'fax');
      if (faxLocator?.phone?.number && faxLocator.phone.purpose == 'fax') {
        recordsGroup.get('fax')?.setValue(faxLocator.phone.number);
      } else {
        recordsGroup.get('fax')?.setValue('');
      }
  
      // Patch email field
      const emailLocator = contact.party?.locators?.find((l: PartyLocator) => l.typeCode === 'email');
      if (emailLocator?.email?.address) {
        recordsGroup.get('email')?.setValue(emailLocator.email.address);
      } else {
        recordsGroup.get('email')?.setValue('');
      }
  
      // Trigger validation after patching values
      recordsGroup.updateValueAndValidity();
    }

  /**
   * Validates request number formats returned from the backend after order submission.
   * Supports both hierarchical format (001, 001-01, 001-02-03) and legacy format (1, 2, 3).
   * Logs parent-child relationships to console for debugging split requests.
   * Displays user warning toast if invalid formats are detected.
   * 
   * @param response - The order response from updateOrder API call
   */
  private validateRequestNumberFormat(response: any): void {
  if (!response?.retrievalObjective?.requests) return;

  const result = this.validatorService.validate(
    response.retrievalObjective.requests
  );

  console.log(
    `[Validation] Total: ${result.totalCount} | ` +
    `Hierarchical: ${result.hierarchicalCount} | ` +
    `Legacy: ${result.legacyCount} | ` +
    `Invalid: ${result.invalidCount}`
  );

  if (result.invalidCount > 0) {
    this.messageService.add({
      severity: 'warn',
      summary: 'Request Number Format Warning',
      detail: `${result.invalidCount} request(s) have unexpected format.`
    });
  }
}
}
